'use client';

import * as React from 'react';
import { TrendingUp } from 'lucide-react';
import {
  formatEventDateRange,
  formatEventTimes,
  formatEventWhen,
  TIME_TBA_LABEL,
  type DiscoverBadge,
  type DiscoverEvent,
  type WhyReason,
} from '@loci/types';
import { minutesSinceStartWithoutEnd } from '@loci/validation';
import type { CityCode } from '@loci/city-editions';
import { SponsoredBadge, SponsoredTooltip } from '@loci/ui';

import { useCitySurface } from '@/lib/city-context.client';
import { buildEventDetailHref } from '@/lib/recommendations/eventHref';
import { cn } from '@/components/atoms/primitives/utils';
import { Icon } from '@/components/atoms/primitives/icon';
import { ResaleMarker } from '@/components/atoms/ResaleMarker';
import { NeighborhoodFootprintMark } from '@/components/atoms/NeighborhoodFootprintMark';
import { resolveNeighborhoodIdentity } from '@/lib/neighborhoods/clt-slugs';
import { resolveHoodmarkPath } from '@/lib/neighborhoods/hoodmarkPath';
import { useNeighborhoodFootprints } from '@/hooks/useNeighborhoodFootprints';
import { classifyLocalHourToTimeOfDaySlug } from '@/lib/discover/timeOfDay';
import { formatSpineClockParts, parseEventInstantMs } from '@/lib/discover/timelineClock';
import { categoryLabel } from '@/config/categories';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';
import { DetailsUnconfirmedBadge } from '@/components/molecules/DetailsUnconfirmedBadge';
import { SponsoredAttribution } from '@/components/molecules/SponsoredAttribution';
import { listedWhyReasons } from '@/lib/discover/listedWhyReasons';
import { discoverRowMetaTokens } from '@/lib/discover/rowMetaLine';
import { discoverRangeLineAddsInformation } from '@/lib/discover/rowRangeLine';
import { DisplayEventTitle } from '@/lib/format/DisplayEventTitle';
import { formatDiscoverRowAccessibleName } from '@/lib/discover/rowPlace';
import { resolveDiscoverPriceShape } from '@/lib/discover/priceShape';
import { PRICE_FREE, PRICE_NOT_LISTED } from '@/lib/copy/priceLabel';
import { mapSponsoredWireTier } from '@/lib/sponsored/tierMap';
import { SponsoredDisclosureCopy } from '@/lib/sponsored/disclosure';
import {
  beaconSponsoredClick,
  warmSponsoredClickCsrf,
  type SponsoredClickSurface,
} from '@/lib/sponsored/clickBeacon';

import { DiscoverSaveButton } from './DiscoverSaveButton';
import { getWhyReasonChipPresentation } from './whyReasonChip';

/**
 * Server already caps `whyReasons` at 3 (FRE-928). This client-side cap is a
 * defensive belt-and-braces guard so a malformed wire payload can never blow
 * the row layout out — the DS row carries at most a 3-tag why-line.
 */
const MAX_WHY_REASON_CHIPS = 3;

/**
 * WEB-788 / PAR-29 — the neutral category-signature fallback.
 *
 * A row whose `categories` array is empty used to render **no signature at
 * all**: no rule, no caps label, just a gap where every other row carries a
 * mark. On the live Charlotte feed that is not an edge case — Eventbrite rows
 * routinely arrive with no category mapped — so the list read as though the
 * signature were randomly missing.
 *
 * O-1's neutral fallback is the governing pattern: an unmapped category
 * inherits the neutral `--cat: var(--fg-2)` rather than an invented hue. The
 * same rule applies one level up — the row takes a neutral *label* rather
 * than an invented category. `Event` invents nothing: it is the genus every
 * row on this surface belongs to, and
 * it is the honest thing to say when the species is unknown. Deliberately NOT
 * `UNCATEGORIZED`, which reports our data-quality state to a reader who
 * cannot act on it.
 */
const UNCATEGORIZED_SIGNATURE_LABEL = 'Event';

/**
 * How many DEFERRING secondary categories the signature prints (WEB-672 /
 * DPL-22). Applied to the *resolved* labels, never to the raw slugs — see the
 * pipeline in the component body.
 */
const MAX_SECONDARY_CATEGORIES = 2;

export interface DiscoverEventCardProps {
  /** Canonical view-model for the row. */
  event: DiscoverEvent;
  /** City segment used to build the `/event/<slug>` detail link. */
  cityCode: CityCode;
  /** Additional CSS classes merged onto the row anchor. */
  className?: string;
  /**
   * Surface this card renders on, used to attribute a sponsored-card click
   * (API-1366). Only consumed when `event.sponsored` is present; defaults to
   * `'discover'` (the primary M1 sponsored surface — category pages are the
   * same `/discover` route). Pass `'category'` from the category surface to
   * label those clicks distinctly.
   */
  sponsoredSurface?: SponsoredClickSurface;
  /**
   * Shared timeline instant (WEB-779). The list forwards the Discover clock
   * so countdown, grouping, and the NOW marker stay on one timestamp. `null`
   * is provider-pending (no local sample). Omitted (`undefined`) samples the
   * client clock once after mount (WEB-670 / standalone stories).
   */
  now?: Date | null;
  /**
   * WEB-779 — IANA zone for the spine clock face. The list passes the city
   * zone so start time, countdown, grouping, and the NOW marker share one
   * timezone. Falls back to `event.timezone` when omitted (isolated stories
   * / card tests).
   */
  timeZone?: string;
}

interface TagChipModel {
  readonly label: string;
  readonly variant: 'hot' | 'sold-out';
}

function displayCategoryLabel(label: string | null): string | null {
  return label == null ? null : categoryDisplayLabel(label);
}

/**
 * WEB-1228 (R10): `top-pick`, `editors-pick` and `dont-sleep` ("Rising") map
 * to no chip. No editor writes them, so a row never prints an editorial
 * label. Only the inventory signal survives.
 */
function badgeToTag(badge: DiscoverBadge): TagChipModel | null {
  switch (badge) {
    case 'almost-sold-out':
      return { label: 'ALMOST GONE', variant: 'hot' };
    default:
      return null;
  }
}

/** Minute in ms — the unit every relative-time affordance on this row uses. */
const MS_PER_MINUTE = 60_000;

/**
 * How far ahead a start time may be and still earn an `IN N MIN` sub-line.
 * Past this the countdown stops being actionable and the clock face alone
 * carries the meaning, so the sub-line renders nothing rather than a
 * technically-true `IN 214 MIN`.
 *
 * One hour, read off the handoff rendering rather than picked: at a page time
 * of 8:42 PM it shows `IN 18 MIN` on the 9:00 PM rows but `LATE` on the
 * 10:00 PM rows. 78 minutes therefore has to fall OUTSIDE the window, which
 * rules out the wider values.
 */
const IMMINENT_WINDOW_MINUTES = 60;

interface RowTiming {
  /** The event's start time has passed. */
  readonly hasStarted: boolean;
  /**
   * The event is running right now — started, and a server-supplied `endsAt`
   * has not yet passed. Events with no `endsAt` are never marked live: without
   * an end time nothing on the wire says it is still going, and asserting
   * "On now" from a start time alone is exactly the fabrication this project
   * forbids.
   */
  readonly isLive: boolean;
  /** Whole minutes until start; negative once started. */
  readonly minutesUntilStart: number;
  /** Whole minutes since start, or `null` when it has not started. */
  readonly minutesSinceStart: number | null;
  /**
   * API-1820 / R5 — whole minutes since start for a started row with NO
   * `endsAt`, while it is inside the one global 3 h default the API admits it
   * for (`STARTED_WITHOUT_END_DEFAULT_MS`); otherwise `null`. Reads
   * "Started N min ago" — a fact about the start, never an "On now" claim.
   */
  readonly startedWithoutEndMinutes: number | null;
}

function computeRowTiming(startsAt: string, endsAt: string | undefined, now: number): RowTiming {
  const startMs = parseEventInstantMs(startsAt);
  if (Number.isNaN(startMs)) {
    return {
      hasStarted: false,
      isLive: false,
      minutesUntilStart: Number.NaN,
      minutesSinceStart: null,
      startedWithoutEndMinutes: null,
    };
  }

  const endMs = endsAt ? parseEventInstantMs(endsAt) : Number.NaN;
  const hasStarted = now >= startMs;
  const isLive = hasStarted && !Number.isNaN(endMs) && now < endMs;

  return {
    hasStarted,
    isLive,
    minutesUntilStart: Math.floor((startMs - now) / MS_PER_MINUTE),
    minutesSinceStart: hasStarted ? Math.floor((now - startMs) / MS_PER_MINUTE) : null,
    startedWithoutEndMinutes: endsAt ? null : minutesSinceStartWithoutEnd(startMs, now),
  };
}

/** `1h 12m` / `47m` — the elapsed figure in the live row's ember pill. */
function formatElapsed(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

/**
 * Read the client clock once, after mount.
 *
 * Returns `null` on the server and on the very first client render, so every
 * now-derived affordance (live styling, countdown, elapsed pill) is absent in
 * the SSR HTML and in hydration. Deriving them during render instead would
 * make the server and client markup disagree by however many milliseconds
 * separate the two, which is a React hydration error, not a cosmetic drift.
 *
 * Sampled once rather than ticked: WEB-671 (DPL-20) introduces the shared live
 * clock that drives the NOW marker, and one interval per row — 50+ timers on a
 * full page — is the wrong way to get there.
 */
function useClientNow(): number | null {
  const [now, setNow] = React.useState<number | null>(null);
  React.useEffect(() => {
    setNow(Date.now());
  }, []);
  return now;
}

/**
 * WEB-673 / DPL-23 — tracks whether the signal-pill rail actually overflows
 * its own width. Drives the ≤720 right-edge mask: the mask must NOT clip the
 * final pill when the set fits without scrolling (an unconditional mask
 * would visually truncate a short pill set that never needs to scroll).
 *
 * `ResizeObserver`-driven rather than a one-shot mount measurement: the rail
 * width and pill count can both change after mount (window resize, a
 * `whyReasons`-bearing fetch resolving), and a stale reading would leave the
 * mask either missing when it should show or stuck when the content no
 * longer overflows.
 */
function useRailOverflow<T extends HTMLElement>(): [React.RefObject<T | null>, boolean] {
  const ref = React.useRef<T | null>(null);
  const [overflows, setOverflows] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const check = (): void => {
      setOverflows(el.scrollWidth > el.clientWidth + 1);
    };
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  });

  return [ref, overflows];
}

/**
 * DiscoverEventCard — list-row molecule for the `/discover` list view.
 *
 * 3-track grid (`112px minmax(0,1fr) auto`):
 *   - Spine column (112px): hoodmark glyph + hood label + clock face + sub-line.
 *     WEB-669 (DPL-21) retired the per-row taste-match ring that used to lead
 *     this column; WEB-670 put the hoodmark in the slot it vacated.
 *   - Body: category signature (WEB-672 — neutral `EVENT` fallback when the
 *     row resolves no category, WEB-788) + title (h3) + the single mono meta
 *     line (`VENUE · capacity`, WEB-788/DPL-19; WEB-1228 dropped distance and
 *     walk) + the unified signal-pill rail (WEB-673 — live "On now" pill +
 *     FRE-910 why-reason tags minus `PROXIMITY`) + status chip row
 *     (almost gone / sold out; WEB-1228 removed the editorial chips).
 *   - Actions column (auto): the price block (WEB-674 — single / FROM+range+
 *     tiers / FREE+condition / unknown) stacked above a row holding the
 *     TICKETS/RSVP quick action (WEB-675) beside the save toggle.
 *
 * The row exposes exactly one `<a>` — on the title, inside the semantic `<h3>`
 * — pointing at `buildEventDetailHref(cityCode, slug, surface)` (WEB-772:
 * same-origin on city / fallback, city-host on app). `.dv-ev__link::after`
 * stretches its hit area over the whole row, so clicking anywhere navigates
 * while Tab still stops once per row. Controls that must stay independently
 * operable (save toggle, sponsored disclosure) are DOM siblings lifted above
 * the stretched link with `z-index`, never descendants of it: a `<button>`
 * inside an `<a>` is invalid HTML and an axe `nested-interactive` violation.
 *
 * Pure presentational component — `DiscoverEvent` is the contract. All
 * personalization fields (`whyReasons`, `capacity`) are
 * server-derived and render with honest-absence: omitted ⇒ the affordance is
 * not drawn (never a fabricated `0`). Status chips are likewise
 * server-derived; this card only renders what is on the event.
 */
export function DiscoverEventCard({
  event,
  cityCode,
  className,
  sponsoredSurface = 'discover',
  now: nowProp,
  timeZone,
}: DiscoverEventCardProps): React.ReactElement {
  // API-1366 — fire a best-effort sponsored-click beacon when a promoted
  // (sponsored) card is clicked, before the anchor navigates cross-host. The
  // beacon `keepalive`s so it survives the navigation; it is a no-op for
  // organic rows (no `event.sponsored`). The backend derives the viewer
  // identity server-side — this never sends one.
  // Depend on the primitive id, not the `sponsored` object: `event` is
  // recreated on every fetch update, so the object identity churns even when
  // the listing is unchanged — which would re-run the effect and rebuild the
  // callback on every render.
  const sponsoredListingId = event.sponsored?.listingId;
  // The click POST is CSRF-protected, and the double-submit header can only be
  // read from a cookie the browser already holds. On an anonymous Discover load
  // the events fetch happens server-side, so nothing warms `_csrf` and every
  // click would 403 into silence. Warm it once, on mount, only for sponsored
  // rows — the click itself is mid-navigation and cannot wait for a round-trip.
  React.useEffect(() => {
    if (!sponsoredListingId) return;
    warmSponsoredClickCsrf();
  }, [sponsoredListingId]);

  const handleCardClick = React.useCallback(() => {
    if (!sponsoredListingId) return;
    beaconSponsoredClick({
      listingId: sponsoredListingId,
      surface: sponsoredSurface,
      clickTarget: 'card',
    });
  }, [sponsoredListingId, sponsoredSurface]);
  const times = React.useMemo(
    () => formatEventTimes(event.startsAt, event.endsAt, event.timezone),
    [event.startsAt, event.endsAt, event.timezone],
  );

  // PIP-98 — honor the server-authoritative `startTimeKnown` signal. When
  // the upstream feed only had a calendar date (or the fake-midnight-UTC
  // signature the ingest pipeline couldn't resolve), `startsAt` collapsed
  // to local midnight and `splitLocalTime` would render a confident "12"
  // (the "ACC Baseball Championship at 5 AM" class of bug). Instead we show
  // the venue-local short date in the big block and "Time TBA" in the
  // sub-line — no fabricated clock value. `formatEventWhen` is the single
  // server-timezone-keyed source of truth shared with the detail hero and
  // category listing.
  // Back-compat: a missing `startTimeKnown` means "known" (the field was
  // added by PIP-98; older cached/serialized events omit it). Defaulting to
  // `false` would mislabel valid timed events as "Time TBA".
  const isTimeKnown = event.startTimeKnown ?? true;
  const when = React.useMemo(
    () => formatEventWhen(event.startsAt, event.timezone, isTimeKnown),
    [event.startsAt, event.timezone, isTimeKnown],
  );

  // WEB-779 — spine face comes from the shared formatter (city zone when the
  // list supplies `timeZone`), not `formatEventTimes` + a local 24h split.
  const spineZone = timeZone ?? event.timezone;
  const startParts = React.useMemo(
    () => formatSpineClockParts(event.startsAt, spineZone),
    [event.startsAt, spineZone],
  );
  const { clock, period } = startParts;

  // Big block: the start clock face when known; the venue-local short date
  // (e.g. "MON MAY 18") when the time is TBA so the card is still scannable.
  const primaryTime = isTimeKnown ? clock : when.dateLine;

  // WEB-670 — everything below is derived from the *client* clock and is
  // therefore `null` until after mount (see `useClientNow`). A row renders as
  // a plain upcoming row on the server and gains its live/countdown character
  // on the client; it never renders a state the server could not have agreed
  // with.
  const clientNow = useClientNow();
  const now = nowProp instanceof Date ? nowProp.getTime() : nowProp === null ? null : clientNow;
  const timing = now === null ? null : computeRowTiming(event.startsAt, event.endsAt, now);
  // A TBA start has no trustworthy clock value, so it can never be "on now" —
  // `startsAt` is a fabricated local midnight in that case (PIP-98).
  const isLive = isTimeKnown && (timing?.isLive ?? false);

  // WEB-670 — the AM/PM period moved OUT of the sub-line and into a mono
  // superscript on the clock face itself, which frees the sub-line to carry
  // the row's temporal state instead of a value the big block already implies
  // (the WEB-292 "exactly one AM/PM element" rule still holds — it is now the
  // superscript rather than this line).
  //
  // Multi-day: the opaque "PM · → MAY 22" arrow notation never told the
  // reader whether the event starts, ends, or *spans* those days. WEB-293
  // replaces it with the explicit two-sided range from `formatEventDateRange`
  // ("Wed, May 20 · 5:00 PM → Fri, May 22 · 11:00 PM EDT"), which carries
  // weekday + date on both sides. `formatEventDateRange` is fail-soft
  // (returns '' on unparseable input), so it degrades to no sub-line.
  //
  // Time-unknown: the sub-line is the explicit "Time TBA" affordance (the
  // big block already carries the date), never a fabricated period.
  //
  // The handoff's third state, `DOORS 7:00`, is deliberately NOT implemented:
  // no doors time exists on the discover wire today. API-1668 projects it; a
  // door time invented from the start time would be a fabrication. Until then
  // the sub-line simply omits it.
  // `LATE` is a property of the EVENT, not of the reader's clock: it marks a
  // late-night start, derived from the venue-local start hour through
  // `classifyLocalHourToTimeOfDaySlug` (`late` = hour >= 21 or hour < 5,
  // wrapping midnight — the backend's own bucket rule).
  //
  // It deliberately does NOT mean "you missed the start". The handoff settles
  // it — both of its LATE rows start at 10:00 PM.
  const startHour24 = startParts.hour24;
  const isLateNight =
    isTimeKnown &&
    Number.isFinite(startHour24) &&
    classifyLocalHourToTimeOfDaySlug(startHour24) === 'late';

  const subTime = ((): string | null => {
    if (!isTimeKnown) return TIME_TBA_LABEL;
    // Imminence outranks the late-night marker: a 9:00 PM show 18 minutes out
    // is a countdown, not a category. (The handoff renders exactly that.)
    // `timing` is null pre-mount, so the countdown is client-only; `LATE`
    // below is not, and survives SSR.
    if (
      timing &&
      !timing.hasStarted &&
      timing.minutesUntilStart >= 0 &&
      timing.minutesUntilStart <= IMMINENT_WINDOW_MINUTES
    ) {
      return `IN ${timing.minutesUntilStart} MIN`;
    }
    // `LATE` is a PLANNING affordance — "this one runs late", useful while you
    // are still deciding whether to go. Once the event has started it stops
    // being that and becomes ambiguous in the worst possible way: a running
    // 10 PM show would render `LATE` beside the `On now · 30m in` pill, and
    // both readings of the word ("it's a late-night event" / "you're late")
    // would apply at once. That is precisely the collision this token was
    // rewritten to avoid, so it is gated on not-yet-started.
    //
    // `timing` is null until mount, so SSR renders the planning label and the
    // client withdraws it if the event turns out to have begun.
    if (isLateNight && !timing?.hasStarted) return 'LATE';
    // A started event says nothing here — the live ember pill already states
    // it is on now and how far in, and inventing a second token for the same
    // fact is how the LATE collision happened in the first place.
    return null;
  })();

  // WEB-293's explicit two-sided range ("Wed, May 20 · 5:00 PM → Fri, May 22 ·
  // 11:00 PM EDT") is preserved, but WEB-670 moves it OUT of the spine and
  // into the body.
  //
  // It has to move: `isMultiDay` is true for any event crossing midnight — a
  // 9:39 PM show ending at 12:39 AM is the common evening case, not an edge
  // case — and that string wraps to four lines inside a 112px column, which
  // triples the row height and buries the clock face. The body column has the
  // width for it. The spine sub-line is for short state tokens only.
  //
  // WEB-788 / PAR-28 narrows the gate. `times.isMultiDay` is true for ANY
  // crossing of local midnight, so a 10:15 PM show ending at 12:15 AM — the
  // ordinary evening case — drew a full `TUE, AUG 18 · 10:15 PM → WED, AUG 19
  // · 12:15 AM EDT` line restating a start the spine already prints and a date
  // the day heading already prints. `discoverRangeLineAddsInformation` keeps
  // the line for a genuine multi-day run and withdraws it for a same-night
  // rollover. Both conditions must hold: the formatter still needs `isMultiDay`
  // to produce a two-sided string.
  const rangeAddsInformation = React.useMemo(
    () =>
      discoverRangeLineAddsInformation({
        startsAt: event.startsAt,
        endsAt: event.endsAt,
        timeZone: event.timezone,
      }),
    [event.startsAt, event.endsAt, event.timezone],
  );
  const multiDayRange =
    isTimeKnown && times.isMultiDay && event.endsAt && rangeAddsInformation
      ? formatEventDateRange(event.startsAt, event.endsAt, event.timezone) || null
      : null;

  // `On now · 1h 12m in` — the live row's ember pill. Only ever built from a
  // real `endsAt`-bounded live window (see `computeRowTiming`).
  const liveElapsedLabel =
    isLive && timing?.minutesSinceStart !== null && timing?.minutesSinceStart !== undefined
      ? `On now · ${formatElapsed(timing.minutesSinceStart)} in`
      : null;

  // WEB-787 — the discover wire may carry a display name *or* a stored slug
  // (label cache fails open). Resolve both forms so a kebab token never
  // reaches copy, and so a slug-valued row still finds a footprint.
  //
  // The glyph always draws: a unique seed-projected path when the hood is
  // known, the API path when it is denser than the seed, and the documented
  // rectangle fallback otherwise. WEB-781's empty-slot rule left the spine
  // blank and let a shared hexagon stand in on hosts that never received
  // API-1657 boundaries.
  const neighborhood = resolveNeighborhoodIdentity(event.neighborhood);
  const footprint = useNeighborhoodFootprints(
    neighborhood.slug ? cityCode : undefined,
    neighborhood.slug ?? undefined,
  );
  const hoodmarkPath = resolveHoodmarkPath({
    slug: neighborhood.slug,
    apiPath: footprint.status === 'loaded' ? footprint.path : null,
  });

  // Drives the `--cat` cascade in `styles/components/discover-row.css`.
  // Absent / unrecognised leaves the attribute off, so the row inherits the
  // neutral `--fg-2` fallback rather than an invented hue (DES-63).
  //
  // WEB-788 / PAR-29 — "unrecognised" is resolved against the taxonomy
  // (`categoryLabel`), not merely against `undefined`. A slug the taxonomy does
  // not carry has no hue and no label, so it must take the same neutral path an
  // absent category takes; treating it as resolved was what let a raw wire
  // token reach both `data-cat` and the visible mark.
  const primaryCategorySlug = event.categories[0];
  const primaryCategoryLabel =
    primaryCategorySlug === undefined
      ? null
      : displayCategoryLabel(categoryLabel(primaryCategorySlug));
  const categoryHueSlug = primaryCategoryLabel === null ? undefined : primaryCategorySlug;

  // WEB-672 / DPL-22 — up to two DEFERRING secondaries (9px, `--fg-3`, no
  // category-color mix). A fourth-plus category is silently dropped, never
  // truncated with an ellipsis — the signature is a mark, not a full listing
  // of every tag the event carries. Unresolvable secondaries drop out for the
  // same reason the primary does.
  //
  // The cap runs AFTER the resolve-and-filter, not before it: capping the raw
  // slugs first lets an unresolvable one consume a slot and hide a resolvable
  // category behind it, so a row with an unmapped second category would defer
  // one label where it holds two. Same filter-before-cap rule the why-reason
  // rail follows.
  const secondaryCategoryLabels = event.categories
    .slice(1)
    .map((category) => displayCategoryLabel(categoryLabel(category)))
    .filter((label): label is string => label !== null)
    .slice(0, MAX_SECONDARY_CATEGORIES);

  // The `free` badge was previously filtered out because the right-hand
  // price column was the single canonical FREE surface. With the price
  // column removed, the chip row no longer competes with anything — but
  // dropping `free` from the chips keeps the card focused on the inventory
  // signal (almost-sold-out) instead of duplicating the category-level free
  // filter.
  //
  // WEB-547 / D3: lifecycle `SOLD_OUT` is browse-visible and needs an explicit
  // chip (the `almost-sold-out` badge is a different signal). Prefer
  // the lifecycle chip and suppress the softer "ALMOST GONE" when both land.
  const isSoldOut = event.status === 'SOLD_OUT';
  const statusTags = event.badges
    .filter((badge) => badge !== 'free')
    .filter((badge) => !(isSoldOut && badge === 'almost-sold-out'))
    .map((badge) => badgeToTag(badge))
    .filter((chip): chip is TagChipModel => chip !== null);
  const tags: TagChipModel[] = isSoldOut
    ? [{ label: 'SOLD OUT', variant: 'sold-out' }, ...statusTags]
    : statusTags;

  const surface = useCitySurface();
  const href = buildEventDetailHref(cityCode, event.slug, surface);
  // WEB-780 — the link's accessible name uses the honest place tokens
  // (`Title — Venue, Hood`); an unresolved `venueKind` drops the junk venue
  // label and a fully unknown place falls back to the title alone. The VISIBLE
  // place line is no longer separate: WEB-788 folds the venue into the single
  // mono meta line below (`metaTokens`), so the announced name and the printed
  // line no longer share a formatter.
  const accessibleName = formatDiscoverRowAccessibleName(event.title, event);

  // WEB-674 / DPL-24 — the row's honest price shape. See `resolveDiscoverPriceShape`
  // for the full branch order; the one rule that matters at every call site is
  // that `priceKnown === false` can NEVER render as Free, even from a legacy
  // fabricated `priceMin: 0`.
  const priceShape = resolveDiscoverPriceShape({
    priceKnown: event.priceKnown,
    priceMin: event.priceMin,
    priceMax: event.priceMax,
    priceTierCount: event.priceTierCount,
    plusFees: event.plusFees,
    isFree: event.isFree,
    currency: event.currency,
    ctaType: event.ctaType,
  });

  // WEB-675 / DPL-25 — the TICKETS / RSVP quick action. Label from
  // `ctaType`, never derived client-side from the presence of a URL (the
  // type file is explicit: browse carries `buy | rsvp | free-entry |
  // sold-out`, and the button renders from that vocabulary, not by guessing
  // from a link).
  //
  // Gates on `ctaHref`, NOT `ticketUrl`: `ticketUrl` is a REQUIRED string on
  // `DiscoverEvent` (never absent), so it cannot express "no honest
  // destination" the way `ctaHref` can — `ctaHref` is `null` exactly when
  // there is no honest destination (a non-actionable `ctaType`, a dead link
  // the PIP-163 sweeper flagged, or a free event with no page), and the
  // type's own doc says to "render a disabled affordance, never a dead #."
  // Omitting the quick action IS that disabled affordance here — there is no
  // href-less variant of this control to disable in place.
  const quickActionLabel: 'TICKETS' | 'RSVP' | null =
    event.ctaType === 'buy' ? 'TICKETS' : event.ctaType === 'rsvp' ? 'RSVP' : null;
  const quickActionHref = event.ctaHref;
  const showQuickAction =
    quickActionLabel !== null &&
    typeof quickActionHref === 'string' &&
    quickActionHref.trim().length > 0;
  // WEB-944 / DES-78 — lead with the CTA when price is unknown. Majority
  // unknown+CTA rows put `.dv-ev__actions-row` first in the DOM (desktop
  // visual = CTA on top). ≤720 reverses that visually via
  // `.dv-ev__r--unk-cta { flex-direction: row-reverse }` so price sits left
  // and the CTA still lines up on the right. Unknown with no CTA stays
  // price-then-save. Single / tiered / free keep price-then-actions.
  const leadUnknownWithCta = priceShape.kind === 'unknown' && showQuickAction;
  const quickActionAccessibleName =
    quickActionLabel === 'TICKETS'
      ? `Buy tickets for ${event.title} (opens in a new tab)`
      : `RSVP for ${event.title} (opens in a new tab)`;

  // WEB-669 / DPL-21 — `event.tasteMatchPct` is deliberately NOT read here.
  // Discovery v2's first governing rule is that taste is expressed only as
  // reasons, never as invented numbers: scores cluster tightly until a profile
  // is rich, so a per-row dial reads as precision the ranking does not possess.
  // The field still rides the wire (the rail's profile-level taste card and the
  // pick set are the two honest homes for it) — this row simply ignores it.

  // FRE-910 — per-event "why" reason tags, honest-absence: `whyReasons` is
  // authenticated-only and omitted (never `[]`) when no qualifying reason
  // exists. A present non-empty array renders as capped themed tags; absent ⇒
  // no tags (the anonymous / no-reason state). The server already caps at 3;
  // `MAX_WHY_REASON_CHIPS` is a defensive client cap.
  //
  // WEB-788 / PAR-28 drops `PROXIMITY` here. DPL-23's division of labour is
  // that pills carry *reasons*, and the proximity pill was only ever a
  // distance fact — `1 MI AWAY` — wearing a reason's clothes. WEB-1228 (R2)
  // keeps distance off the row entirely. The filter runs BEFORE
  // the cap so dropping it promotes a real taste reason into the visible three
  // rather than leaving a hole. A row whose only reason was proximity is left
  // with no taste pill — correctly: being near you is not a taste signal.
  const whyReasons: readonly WhyReason[] = listedWhyReasons(event.whyReasons).slice(
    0,
    MAX_WHY_REASON_CHIPS,
  );
  // WEB-1078 — positive signals only. A row with no reason to show prints no
  // per-row absence pill (the retired dashed "No taste signal yet" chip); the
  // list tail states the absence once for the whole list. When every pill is
  // absent for this viewer, the rail node is not rendered at all — no empty
  // container, no phantom gap in the row rhythm. "On now" is a status pill,
  // so a live row with no taste reason still keeps its rail.
  // API-1820 / R5 — `Started 20 min ago`: a started row with no end time.
  const startedAgoLabel =
    isTimeKnown && timing?.startedWithoutEndMinutes != null
      ? `Started ${timing.startedWithoutEndMinutes} min ago`
      : null;

  const hasSignalPills =
    liveElapsedLabel !== null || startedAgoLabel !== null || whyReasons.length > 0;
  const [railRef, railOverflows] = useRailOverflow<HTMLDivElement>();

  // WEB-788 / DPL-19 — the row's one meta line: `VENUE · capacity`, in mono,
  // in that order, on every row (WEB-1228 / R2 dropped distance and walk
  // time). Assembly (including which facts are honest to print) lives in
  // `discoverRowMetaTokens` so the shape is unit-testable and the card holds
  // no formatting rules of its own.
  const metaTokens = React.useMemo(
    () =>
      discoverRowMetaTokens({
        venueName: event.venueName,
        venueKind: event.venueKind,
        capacity: event.capacity,
      }),
    [event.venueName, event.venueKind, event.capacity],
  );

  // WEB-450 — "Artist you know" why-line. Authenticated-only + honest-absence:
  // the server attaches `matchedArtist` only when a lineup artist the connected
  // viewer follows performs at this event; anonymous / no-match rows omit it and
  // the caption is not drawn. Mirrors the home-rail EventRailCard treatment (a
  // teal mono caption with a `▲ ` prefix, above the title). A blank name
  // collapses to `null` so a malformed payload never prints a bare "YOU FOLLOW".
  // `|| null` (not `??`): a whitespace-only name trims to '' (falsy) and must
  // collapse to absent, never print a bare "YOU FOLLOW".
  const matchedArtistName = event.matchedArtist?.name.trim() || null;

  // WEB-36 — sponsored-listing disclosure pill. Honest-absence:
  // `event.sponsored` is only ever present on a row the server-side
  // `injectSponsoredSlots` injector actually promoted; organic rows never
  // carry the field, so this renders nothing extra for the overwhelming
  // majority of cards. Unlike `SponsoredBadge`'s own docstring guidance (top
  // -right, absolutely positioned over hero media), this list row has no
  // poster image to overlay — the pill visually leads the body column,
  // left-aligned (WEB-535 renders it in an overlay cluster outside the row
  // anchor, over an in-flow spacer — see the wrapper-level comment below).
  // It is unconditionally rendered (no hover gate) per FTC 16 CFR Part 255.
  //
  // WEB-537 — Growth/Featured+ escalation (DES-50) is now built (the earlier
  // "M2 scope" deferral shipped here). DES-50 draws these on a poster card;
  // this list row maps them following the same WEB-36/WEB-535 adaptation:
  //   - border → a per-tier LEFT accent rail on the wrapper (`sponsoredRailStyle`)
  //     using the theme-aware `--accent`@40% (Growth) / `--cta` amber (Featured+),
  //     keeping the row's bottom divider + list rhythm instead of a floating box.
  //   - Growth → a "Sponsored by {advertiser}" footer band (last body child).
  //   - Featured+ → a "Presented by {advertiser}" header band under the pill.
  //   The escalation is ADDITIVE — the always-visible pill/label is never
  //   replaced. `advertiserName` is always present; `logoUrl` is honest-absence
  //   (`SponsoredAttribution` renders no image when it is null / CSP-unrenderable).
  const sponsoredMeta = event.sponsored ?? null;
  const sponsoredTier = sponsoredMeta ? mapSponsoredWireTier(sponsoredMeta.tier) : null;

  // Per-tier left accent rail (DES-50 "card border by tier"), mapped onto the
  // full-bleed list row as a left rule so it never collides with adjacent rows'
  // bottom dividers. Theme-aware tokens (not the pill's fixed over-media scrims)
  // because this rail sits on the card `--surface`, not over hero media.
  const sponsoredRailStyle: React.CSSProperties | undefined =
    sponsoredTier === 'growth'
      ? {
          // DES-50 growth border = `--accent` @40%. `--clt-skyline` is the
          // live, theme-aware CLT accent teal the card already uses
          // (`--accent`/`--clt-skyline-wash`); #0E7B7B → #2FA3A3.
          borderLeftWidth: '1.5px',
          borderLeftStyle: 'solid',
          borderLeftColor: 'color-mix(in srgb, var(--clt-skyline) 40%, transparent)',
        }
      : sponsoredTier === 'featured'
        ? {
            // DES-50 featured border = `--cta` (Electric Amber, theme-aware:
            // #CC9300 light → #FFB800 dark).
            borderLeftWidth: '1.5px',
            borderLeftStyle: 'solid',
            borderLeftColor: 'var(--cta)',
          }
        : undefined;

  // WEB-1098 — DS 1.10.0 resale marker (`reference/resale/cards.html`): the
  // first child of the price block, so it sits ABOVE the figure in the ≥721
  // column and INLINE before it in the ≤720 row (`discover-reflow.css`
  // shrinks it to the `--sm` size there). `dv-price--resale` demotes the
  // figure to `--fg-2` / 500. Category signature untouched.
  const isResaleListing = event.resaleListing === true;

  const priceBlock = (
    <p
      className={cn('dv-price', isResaleListing && 'dv-price--resale')}
      data-testid="discover-event-card-price"
    >
      {isResaleListing && <ResaleMarker />}
      {priceShape.kind === 'unknown' && (
        <span className="dv-price__unknown" data-testid="discover-event-card-price-unknown">
          {PRICE_NOT_LISTED}
        </span>
      )}
      {priceShape.kind === 'free' && (
        <>
          <span className="dv-price__figure dv-price__figure--free">{PRICE_FREE}</span>
          {priceShape.condition !== null && (
            <span className="dv-price__condition" data-testid="discover-event-card-price-condition">
              {priceShape.condition}
            </span>
          )}
        </>
      )}
      {priceShape.kind === 'single' && (
        <span className="dv-price__figure">{priceShape.figure}</span>
      )}
      {priceShape.kind === 'tiered' && (
        <>
          <span className="dv-price__from" data-testid="discover-event-card-price-from">
            FROM
          </span>
          <span className="dv-price__figure">{priceShape.figure}</span>
          <span className="dv-price__range" data-testid="discover-event-card-price-range">
            {priceShape.range}
            {priceShape.tierCount !== null && ` · ${priceShape.tierCount} TIERS`}
          </span>
        </>
      )}
      {priceShape.kind !== 'unknown' && priceShape.showFees && (
        <span className="dv-price__fees" data-testid="discover-event-card-price-fees">
          + fees
        </span>
      )}
    </p>
  );

  // WEB-675 / DPL-25 — TICKETS / RSVP beside save. Both operable controls
  // share one row. `.dv-ev__r` already sits above the stretched row link
  // (`z-index: 2` vs `1`); `stopPropagation` is defence in depth.
  const actionsRow = (
    <div className="dv-ev__actions-row" data-testid="discover-event-card-actions-row">
      {showQuickAction && (
        <a
          href={quickActionHref as string}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event_) => event_.stopPropagation()}
          aria-label={quickActionAccessibleName}
          data-testid="discover-event-card-quick-action"
          data-cta-type={event.ctaType}
          className="a-hit-floor dv-quick-action"
        >
          <Icon
            name="ExternalLink"
            size={13}
            strokeWidth={2.25}
            aria-hidden="true"
            className="shrink-0"
          />
          {quickActionLabel}
        </a>
      )}
      <DiscoverSaveButton
        eventId={event.id}
        eventSlug={event.slug}
        eventTitle={event.title}
        size="row"
      />
    </div>
  );

  return (
    // CodeRabbit (2026-05-23): separate link + save-button hit areas. The
    // previous layout absolutely positioned the save button on top of the
    // full-row anchor — the two interactive targets shared space, which
    // violates the WCAG 2.2 / mobile guidance to maintain ≥8px separation
    // between adjacent 44×44 targets and risked accidental navigation when a
    // user missed the save toggle by a few pixels. The wrapper is now a flex
    // row with two DOM siblings: the `<a>` row (own hit area) on the left and
    // the save-button column (own hit area, padded for spacing) on the right.
    // No overlay, no absolute positioning over the link.
    <div
      className={cn('dv-ev', isLive && 'dv-ev--live', className)}
      data-testid="discover-event-card-wrapper"
      // Drives the `--cat` cascade (styles/components/discover-row.css
      // `.dv-ev[data-cat=…]`). Omitted when the event carries no category, so
      // the row inherits the neutral `--fg-2` spine rather than an invented hue.
      data-cat={categoryHueSlug}
      data-live={isLive ? 'true' : undefined}
      data-sponsored-tier={sponsoredTier ?? undefined}
      style={sponsoredRailStyle}
    >
      {/*
        ─── Spine column (112px) ────────────────────────────────────────────
        The 2px left rule is the time spine. Reading order is deliberate:
        hoodmark → hood label → clock face → sub-line, i.e. *where* then
        *when*, matching the visual stack.
      */}
      <div className="dv-ev__t" data-testid="discover-event-card-lead">
        {/*
          WEB-787 — the glyph is decorative (the hood label names the place),
          so the SVG stays unnamed. Always draw a mark: unique seed path when
          the hood is known, neutral rectangle when it is not. Never a shared
          hexagon and never an empty spine column.
        */}
        <div className="dv-hood" data-testid="discover-event-card-hood">
          <NeighborhoodFootprintMark
            path={hoodmarkPath}
            size={44}
            className="dv-hoodmark"
            data-testid="discover-event-card-hoodmark"
          />
          {neighborhood.displayName.length > 0 ? (
            <span className="dv-hoodmark__l" data-testid="discover-event-card-hood-label">
              {neighborhood.displayName}
            </span>
          ) : null}
        </div>
        <div className="dv-ev__clock" data-testid="discover-event-card-time">
          <span
            className={cn(
              'dv-ev__time',
              // The TBA fallback ("TUE, MAY 19") is far longer than a clock
              // face, so it drops to the mono treatment to stay inside 112px.
              !isTimeKnown && 'dv-ev__time--tba',
            )}
            data-testid="discover-event-card-hour"
          >
            {primaryTime}
            {isTimeKnown && (
              <em className="dv-ev__meridiem" data-testid="discover-event-card-meridiem">
                {period}
              </em>
            )}
          </span>
          {subTime !== null && (
            <span className="dv-ev__sub" data-testid="discover-event-card-time-meta">
              {subTime}
            </span>
          )}
        </div>
      </div>
      {/*
          ─── Body column ──────────────────────────────────────────────────
          At ≤720 every child is placed via class hooks: `.dv-ev__pre` (sponsored,
          why-line — above the title), `.dv-ev__h` / `.dv-ev__m` for title +
          meta, `.dv-ev__post` for the extras below the pills. WEB-1082 moves
          `.dv-catsig` to the last row, beside [Tickets] [save]. Desktop is
          unchanged — the hooks are names only until the `max-width: 720px`
          block in styles/overrides/discover-reflow.css (`@layer overrides`,
          WEB-857 — it must outrank `truncate` on the title).
        */}
      <div className="disc-event__body dv-ev__b" data-testid="discover-event-card-body">
        {/*
            WEB-36/WEB-535 — sponsored disclosure. The "?" tooltip trigger is a
            <button>, which may never nest inside the row link. Under the old
            row-spanning <a> that forced an absolutely-positioned overlay
            cluster pinned to a magic `left-[126px]`, plus an in-flow spacer to
            reserve its height.
            WEB-670 retires both: the row link is now a stretched pseudo-element
            on the title (`.dv-ev__link::after`), not an ancestor, so this
            cluster is a plain in-flow child of the body column. `relative` +
            `z-2` lifts it above the stretched link so the "?" stays clickable
            while the rest of the row still routes to the event.

            `w-fit` is load-bearing: `.dv-ev__b` is a flex COLUMN with no
            `align-items`, so a bare child stretches the FULL body width. With
            `z-2` on top, that would lay a ~900px transparent strip across the
            stretched row link and swallow every click along the pill's line.

            `pointer-events-none` + `pointer-events-auto` on the tooltip keeps
            WEB-535's rule intact: only the "?" is interactive, and a click on
            the badge or the gap falls through to `.dv-ev__link::after`
            underneath and navigates, exactly like the rest of the row.
          */}
        {sponsoredTier !== null && (
          <div
            className="dv-ev__pre pointer-events-none relative z-[2] flex w-fit items-center gap-1.5"
            data-testid="discover-event-card-sponsored"
          >
            <SponsoredBadge tier={sponsoredTier} />
            <SponsoredTooltip
              tier={sponsoredTier}
              triggerVariant="surface"
              className="pointer-events-auto"
            >
              <SponsoredDisclosureCopy />
            </SponsoredTooltip>
          </div>
        )}
        {/*
            WEB-537 — Featured+ "Presented by {advertiser}" header band (DES-50).
            Sits directly under the always-visible disclosure pill (which floats
            over the spacer above), amber-tinted with a bottom rule. Additive to
            the pill, never a replacement. Growth's neutral "Sponsored by" band
            is a FOOTER instead — see the end of this body column.
          */}
        {sponsoredTier === 'featured' && sponsoredMeta && (
          <SponsoredAttribution
            tier="featured"
            advertiserName={sponsoredMeta.advertiserName}
            logoUrl={sponsoredMeta.logoUrl}
            className="dv-ev__pre rounded-[6px]"
          />
        )}
        {/*
            WEB-672 / DPL-22 — category signature. Replaces the pre-WEB-670
            category pill: a 3×12px rule + the primary category in caps mono,
            tinted via `--cat` (the same custom property the spine and
            hoodmark already read, so an unmapped category falls back to the
            neutral `--fg-2` per DES-63 — `data-cat` is simply omitted for
            one, which is the state that selects the default). Up to
            two secondary categories defer at 9px in `--fg-3` — plain muted
            text, not category-tinted, so the row never looks like it is
            juggling three colored controls.

            A mark, not a filter control: plain text nodes, no role, no
            tabIndex, no href. The one-line flex row never wraps and never
            changes the row's height whether the event carries one category or
            three.

            WEB-788 / PAR-29 — the signature is now UNCONDITIONAL. It used to
            be gated on `primaryCategory !== undefined`, so an uncategorised
            row (routine on the live Eventbrite feed) rendered no rule and no
            label at all, next to a neutral grey glyph — an omission that reads
            as a rendering fault, not as an honest "we don't know". The row now
            falls back to the neutral `EVENT` mark, tinted by the same neutral
            `--cat: var(--fg-2)` default the missing `data-cat` attribute
            already selects, so nothing about the hue is invented either.
            `data-signature="fallback"` exposes the state for tests without
            adding a second class to reason about.

            "Uncategorised" means *unresolvable*, not merely absent: a slug the
            taxonomy does not carry takes the same fallback path, because it
            has neither a hue nor a label to show.
          */}
        <p
          className="dv-catsig"
          data-testid="discover-event-card-category-signature"
          data-signature={primaryCategoryLabel === null ? 'fallback' : 'resolved'}
        >
          <span className="dv-catsig__rule" aria-hidden="true" />
          <span
            className="dv-catsig__primary"
            data-testid="discover-event-card-category-signature-primary"
          >
            {primaryCategoryLabel ?? UNCATEGORIZED_SIGNATURE_LABEL}
          </span>
          {secondaryCategoryLabels.length > 0 && (
            <span
              className="dv-catsig__secondary"
              data-testid="discover-event-card-category-signature-secondary"
            >
              {secondaryCategoryLabels.join(' · ')}
            </span>
          )}
        </p>
        {/*
            WEB-450 — "YOU FOLLOW <ARTIST>" taste why-line, above the title per
            the design (mirrors the home-rail EventRailCard why-caption). Teal
            mono, `▲ ` sigil rendered aria-hidden (decorative), truncated so a
            long artist name never blows the row height. Rendered only when the
            server supplied a matched lineup artist.
          */}
        {matchedArtistName !== null && (
          <p
            data-testid="discover-event-card-why-line"
            className="dv-ev__pre m-0 inline-flex max-w-full items-center gap-1 truncate text-micro leading-tight tracking-[0.04em] text-accent-text font-mono"
          >
            <TrendingUp className="inline size-[1em] shrink-0" aria-hidden="true" />
            {`You follow ${matchedArtistName}`}
          </p>
        )}
        {/*
            The row's single primary target. The <a> sits on the title so its
            accessible name contains the published title (WCAG 2.5.3 Label in
            Name — `accessibleName` is "<title> — <venue>, <hood>" when both
            place tokens resolve). WEB-1135 title-cases a shouting feed title
            for the eye and hides that cased string from the accessible name,
            which stays the title as published.
            `.dv-ev__link::after` stretches its hit area over the whole row.
            One <a> per row means Tab stops once per row, not once per inner
            element.
          */}
        <h3
          className="dv-ev__h m-0 truncate text-h6 font-semibold leading-tight text-loci-text font-display"
          data-testid="discover-event-card-title"
        >
          <a
            href={href}
            aria-label={accessibleName}
            onClick={handleCardClick}
            data-testid="discover-event-card"
            // WEB-1229 — `discover_event_opened` reads the id and position here.
            data-discover-event-id={event.id}
            // Exposes the canonical ISO UTC start time on the DOM so the
            // WEB-279 time-of-day filter E2E can read each card's start hour
            // without parsing the rendered "7 PM" string (which is
            // locale/format-dependent and brittle). The visible rendering is
            // unchanged — this is a test-only data hook. Pair with the card's
            // `timezone` field via the page's
            // `discover-template[data-city-timezone]` attribute.
            data-starts-at-iso={event.startsAt}
            // WEB-771 — painted ring is `.dv-ev__link:focus-visible` in
            // styles/overrides/discover-link-focus.css.
            // WEB-857: it sits in `@layer overrides`, declared after Tailwind's
            // `utilities`, which is the only reason it outranks `outline-none`
            // below. Drop `outline-none` and the rule can retire entirely.
            className="dv-ev__link text-inherit no-underline outline-none"
          >
            <DisplayEventTitle title={event.title} />
          </a>
        </h3>
        {/*
            WEB-788 / DPL-19 / PAR-28 — THE meta line. One mono 11px line
            carrying the row's facts in a fixed order: `VENUE · capacity`.
            WEB-1228 (R2): no distance and no walk time — the query is
            centered on the city, not on the reader.

            The neighborhood is deliberately absent: WEB-781/787 print it in
            the spine beside its footprint glyph at every width, so repeating
            it here would say one thing twice. The link's accessible name
            still announces `Title — Venue, Hood`.

            Renders only when at least one token is present (honest absence —
            no dangling separator, no fabricated `0`), and `flex-wrap` lets a
            long venue push tokens to a second visual line without any token
            truncating another.
          */}
        {metaTokens.length > 0 && (
          <p
            className="dv-ev__m m-0 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-micro leading-tight text-loci-text-muted font-mono tracking-[0.04em]"
            data-testid="discover-event-card-meta"
          >
            {metaTokens.map((token, index) => (
              <React.Fragment key={`${index}:${token}`}>
                {index > 0 && <span aria-hidden="true">·</span>}
                <span>{token}</span>
              </React.Fragment>
            ))}
          </p>
        )}
        {/*
          WEB-293 multi-day range, rehoused from the spine (see the
          `multiDayRange` comment above). Two-sided and explicit so the reader
          knows whether the event starts, ends, or *spans* those dates.
        */}
        {multiDayRange !== null && (
          <p
            className="dv-ev__post m-0 text-micro leading-tight text-loci-text-muted font-mono tracking-[0.04em]"
            data-testid="discover-event-card-date-range"
          >
            {multiDayRange}
          </p>
        )}
        {/*
            WEB-673 / DPL-23 — the unified signal-pill rail. Folds the WEB-670
            live "On now" pill and the FRE-910 why-reason tags into ONE rail.

            Pills are inert `<span>`s, not `<li>`s in a `<ul>` — no popover,
            no nested interactive, never part of the tab ring. `role="group"`
            + `aria-label` on the rail gives assistive tech one announced
            group instead of list semantics that no longer fit non-list
            children.

            WEB-1078 — positive signals only, and collapsed when empty: a row
            with no pill for this viewer renders no rail node at all.
          */}
        {hasSignalPills && (
          <div
            ref={railRef}
            className="dv-sig-rail"
            data-testid="discover-event-card-signal-rail"
            data-overflowing={railOverflows ? 'true' : 'false'}
            role="group"
            aria-label="Why this is matched to you"
          >
            {liveElapsedLabel !== null && (
              <span
                className="dv-sig dv-sig--live"
                data-testid="discover-event-card-live-pill"
                data-accent="ember"
              >
                <Icon
                  name="Clock"
                  size={11}
                  strokeWidth={2.25}
                  aria-hidden="true"
                  className="shrink-0"
                />
                {liveElapsedLabel}
              </span>
            )}
            {startedAgoLabel !== null && (
              <span className="dv-sig" data-testid="discover-event-card-started-pill">
                <Icon
                  name="Clock"
                  size={11}
                  strokeWidth={2.25}
                  aria-hidden="true"
                  className="shrink-0"
                />
                {startedAgoLabel}
              </span>
            )}
            {whyReasons.map((reason) => {
              const { iconName, accent, variant } = getWhyReasonChipPresentation(reason.code);
              return (
                <span
                  key={`${reason.code}:${reason.displayText}`}
                  className="dv-sig"
                  data-testid="discover-event-card-why-tag"
                  data-accent={accent}
                  data-variant={variant}
                >
                  <Icon
                    name={iconName}
                    size={11}
                    strokeWidth={2.25}
                    aria-hidden="true"
                    className="shrink-0"
                  />
                  {reason.displayText}
                </span>
              );
            })}
          </div>
        )}
        {/*
            WEB-451 — honest-labeling affordance. Renders only for
            `LOW_CONFIDENCE` events; renders nothing for STANDARD / PREMIUM or
            when `qualityStatus` is absent (quality gate off). The badge carries
            its own accessible text + icon, so it sits outside the decorative
            (aria-hidden) tag-chip list below.
          */}
        <DetailsUnconfirmedBadge qualityStatus={event.qualityStatus} className="dv-ev__post mt-1" />
        {tags.length > 0 && (
          <ul
            className="dv-ev__post mt-1 flex flex-wrap gap-1.5"
            data-testid="discover-event-card-tags"
            aria-hidden="true"
          >
            {tags.map((tag) => (
              <li
                key={tag.label}
                data-testid={
                  tag.variant === 'sold-out'
                    ? 'discover-event-card-sold-out-chip'
                    : 'discover-event-card-tag'
                }
                data-variant={tag.variant}
                className={cn(
                  'disc-event__tag inline-flex items-center rounded-full px-2 py-0.5',
                  'text-micro font-semibold leading-tight [letter-spacing:0.06em]',
                  tag.variant === 'sold-out' ? 'disc-event__tag--sold-out' : 'disc-event__tag--hot',
                )}
              >
                {tag.label}
              </li>
            ))}
          </ul>
        )}
        {/*
            WEB-537 — Growth "Sponsored by {advertiser}" footer band (DES-50):
            24px advertiser logo (honest-absence when null) + attribution,
            separated by a neutral top rule. Featured+ gets the amber "Presented
            by" band at the top of the column instead; both keep the disclosure
            pill. `mt-2` restores the flush footer feel DES-50 shows (the body
            column's `gap-1` would otherwise crowd the top rule).
          */}
        {sponsoredTier === 'growth' && sponsoredMeta && (
          <SponsoredAttribution
            tier="growth"
            advertiserName={sponsoredMeta.advertiserName}
            logoUrl={sponsoredMeta.logoUrl}
            className="dv-ev__post mt-2"
          />
        )}
      </div>
      {/*
          ─── Actions column ───────────────────────────────────────────────
          Its own grid cell, not an overlay: `.dv-ev__r` carries `position:
          relative; z-index: 2`, which lifts it above the stretched row link
          (`z-index: 1`) so the save toggle is the thing you hit when you aim
          at it. The button is 44×44 via SaveEventButton and stops propagation
          on click — defence in depth if the stacking ever shifts.

          DPL-25 adds the TICKETS / RSVP quick action to this cell and owns its
          z-layering.
        */}
      <div
        className={cn('dv-ev__r', leadUnknownWithCta && 'dv-ev__r--unk-cta')}
        data-testid="discover-event-card-save-cell"
      >
        {/*
            WEB-674 / DPL-24 — the honest price block. Four shapes, all driven
            by `resolveDiscoverPriceShape`: a single figure; FROM + range +
            tier count; FREE + its condition; or the explicit unknown state.
            Never a bare number standing in for a real range.

            WEB-944 / DES-78 — when `leadUnknownWithCta`, this block is the
            second child (quiet line under the CTA). Other shapes keep it first.
          */}
        {leadUnknownWithCta ? actionsRow : priceBlock}
        {leadUnknownWithCta ? priceBlock : actionsRow}
      </div>
    </div>
  );
}
